import React, { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AppPaths, AppRoutes } from '@/routes';
import {
  faTrash,
  faDownload,
  faUpload,
  Icon,
  CustomColumns,
  CustomTable,
} from '@/ui/components';
import { StyledLink, StyledLinkContainer } from '@/ui/globalStyles';
import { dateConverter } from '@/utils/helpers';
import { OrderSummary } from '@/types';
import { useTranslate } from '@/translations';

interface OrderGridDetailSectionProps {
  hasAdminAccess?: boolean;
  dataSource: OrderSummary[];
  deleteBillOfLading(movementId: number): void;
}
export const OrderGridDetailSection: FC<OrderGridDetailSectionProps> = ({
  hasAdminAccess,
  dataSource,
  deleteBillOfLading,
}) => {
  const { t } = useTranslate();

  const columns: CustomColumns<OrderSummary> = useMemo(
    () => [
      {
        dataIndex: 'billOfLading',
        title: 'Bill of Lading',
        width: 260,
        fixed: 'left',
        filter: true,
        sorter: true,
      },
      {
        dataIndex: 'volume',
        title: 'Volume',
        width: 100,
        filter: true,
        sorter: true,
      },
      {
        dataIndex: 'portOfLoading',
        title: 'Port of Loading',
        width: 180,
        filter: true,
        sorter: true,
      },
      {
        dataIndex: 'portOfDestination',
        title: 'Port of Destination',
        width: 180,
        filter: true,
        sorter: true,
      },
      {
        dataIndex: 'transitId',
        title: 'Transit',
        width: 180,
        filter: true,
        sorter: true,
      },
      {
        dataIndex: 'despatchDate',
        title: 'Despatched Date',
        renderType: 'date',
        width: 180,
        filter: true,
        sorter: true,
        render: (value: string) => dateConverter(value),
      },
      {
        dataIndex: 'estimatedArrival',
        title: 'Estimated Arrival',
        renderType: 'date',
        width: 180,
        filter: true,
        sorter: true,
        render: (value: string) => dateConverter(value),
      },
      {
        dataIndex: 'arrivalDate',
        title: 'Arrival Date',
        renderType: 'date',
        width: 180,
        filter: true,
        sorter: true,
        render: (value: string) => dateConverter(value),
      },
      {
        dataIndex: 'billOfLadingFile',
        title: 'Bill of Lading File',
        width: 40,
        render: (value: string, data: OrderSummary) =>
          value ? (
            <StyledLinkContainer>
              <a
                href={value}
                download={data.billOfLading}
                aria-label="Download"
              >
                <Icon icon={faDownload} />
              </a>
              {hasAdminAccess ? (
                <StyledLink
                  onClick={() => deleteBillOfLading(data.movementId!)}
                  aria-label="Delete"
                >
                  <Icon icon={faTrash} />
                </StyledLink>
              ) : null}
            </StyledLinkContainer>
          ) : (
            <StyledLinkContainer>
              {hasAdminAccess ? (
                <Link
                  to={`${AppRoutes.systemAdministration}/${data.movementId}/${AppPaths.uploadFile}`}
                  aria-label="Upload"
                >
                  <Icon icon={faUpload} />
                </Link>
              ) : (
                <span>{t('notApplicable')}</span>
              )}
            </StyledLinkContainer>
          ),
      },
    ],
    [deleteBillOfLading, hasAdminAccess, t],
  );

  return (
    <CustomTable
      dataSource={dataSource}
      columns={columns}
      rowKey="movementId"
    />
  );
};
